import { EventContentArg, MoreLinkContentArg } from '@fullcalendar/core'
import styles from './calendarEvents.module.scss'
import { JSX } from 'react'
import { CalendarView } from './calendarParameters'
import { Call, Check, EditNote, HourglassBottom, ListAlt, People, VideoCameraFront } from '@mui/icons-material'
import { CalendarEventType, EventCalendar } from '@/types/Calendar'
import { getOccasionalSlotsIconTextToJSX } from '@/modules/helper/iconsHelper'
import { EventRegularOverlap, OccasionalSlot } from '@/types/Slots'
import { RateSlot, RateType } from '@/types/StaffRate'
import moment from 'moment-timezone'
import { EventDay } from '@/types/EventDay'
import { TaskNote, TaskNoteType } from '@/types/TaskNote.ts'

export function stringToCalendarView(view: string): CalendarView {
    switch (view) {
        case CalendarView.Current:
            return CalendarView.Current
        case CalendarView.OneDay:
            return CalendarView.OneDay
        case CalendarView.OneWeek:
            return CalendarView.OneWeek
        case CalendarView.OneMonth:
            return CalendarView.OneMonth
        case CalendarView.Agenda:
            return CalendarView.Agenda
        default:
            return CalendarView.Current
    }
}

export function renderEventContent(eventInfo: EventContentArg, currentView: CalendarView): JSX.Element | null {
    switch (currentView) {
        case CalendarView.Current:
        case CalendarView.OneWeek:
        case CalendarView.OneMonth:
        case CalendarView.Agenda:
            return renderEventCurrentView(eventInfo)
        case CalendarView.OneDay:
            return renderEventOneDayView(eventInfo)
        default:
            return null
    }
}

function getBackgroundColor(type: CalendarEventType): string {
    switch (type) {
        case CalendarEventType.REGULAR:
            return styles.Regular
        case CalendarEventType.ABSENCE:
            return styles.Absence
        case CalendarEventType.REGULAR_REQUEST:
            return styles.RegularRequest
        case CalendarEventType.EXPIRED:
            return styles.Expired
        case CalendarEventType.REQUEST:
            return styles.Request
        case CalendarEventType.EVENT:
        case CalendarEventType.TASK_NOTE:
            return styles.Event
        case CalendarEventType.CLOSING_DAY:
            return styles.ClosingDay
        case CalendarEventType.SCHEDULED:
            return styles.Scheduled
        case CalendarEventType.VALIDATED:
            return styles.Validated
        case CalendarEventType.AVAILABLE:
            return styles.Available
        case CalendarEventType.REMAINDER_AVAILABLE:
            return styles.RemainderAvailable
        default:
            return styles.Regular
    }
}

function defineStartAdornmentIcon(props: EventCalendar): JSX.Element | undefined {
    switch (props.type) {
        case CalendarEventType.EVENT:
            return (props.slots as EventDay).booked ? <Check className={styles.Icon} /> : undefined
        case CalendarEventType.TASK_NOTE:
            return defineTaskNoteTypeIcon((props.slots as TaskNote).type)
        case CalendarEventType.AVAILABLE:
        case CalendarEventType.VALIDATED:
        case CalendarEventType.REMAINDER_AVAILABLE:
        case CalendarEventType.EXPIRED:
        case CalendarEventType.SCHEDULED: {
            const slots: OccasionalSlot[] = props.slots as OccasionalSlot[]
            return slots.length > 0 && slots[0].externalIcon
                ? getOccasionalSlotsIconTextToJSX(slots[0].externalIcon, styles.Icon)
                : undefined
        }
        default:
            return undefined
    }
}

function defineTaskNoteTypeIcon(type: TaskNoteType): JSX.Element {
    switch (type) {
        case TaskNoteType.task:
            return <ListAlt className={styles.Icon} />
        case TaskNoteType.appointmentInPerson:
            return <People className={styles.Icon} />
        case TaskNoteType.appointmentVideoconference:
            return <VideoCameraFront className={styles.Icon} />
        case TaskNoteType.note:
            return <EditNote className={styles.Icon} />
        case TaskNoteType.call:
            return <Call className={styles.Icon} />
        default:
            return <ListAlt className={styles.Icon} />
    }
}

function renderEventCurrentView(eventInfo: EventContentArg): JSX.Element {
    const iconJSX: JSX.Element | undefined = eventInfo.event.extendedProps
        ? defineStartAdornmentIcon(eventInfo.event.extendedProps as EventCalendar)
        : undefined

    return (
        <div className={`${styles.CurrentEventContent} ${getBackgroundColor(eventInfo.event.extendedProps.type)}`}>
            <div className={styles.IconAndTitle}>
                {iconJSX && <div className={styles.BadgeIcon}>{iconJSX}</div>}
                <div className={`${styles.Title} ${iconJSX ? '' : styles.TitleWithoutIcon}`}>
                    {eventInfo.event.title}
                </div>
            </div>
            {eventInfo.event.extendedProps.number === '' ? (
                (eventInfo.event.extendedProps.type === CalendarEventType.REGULAR_REQUEST ||
                    eventInfo.event.extendedProps.type === CalendarEventType.REQUEST) && (
                    <div className={styles.BadgeIcon}>
                        <HourglassBottom className={styles.Icon} />
                    </div>
                )
            ) : (
                <div className={styles.Badge}>{eventInfo.event.extendedProps.number}</div>
            )}
        </div>
    )
}

function renderEventOneDayView(eventInfo: EventContentArg): JSX.Element {
    const iconJSX: JSX.Element | undefined = eventInfo.event.extendedProps
        ? defineStartAdornmentIcon(eventInfo.event.extendedProps as EventCalendar)
        : undefined

    return (
        <div className={`${styles.DayEventContent} ${getBackgroundColor(eventInfo.event.extendedProps.type)}`}>
            <div className={styles.IconAndTitle}>
                {iconJSX && <div className={styles.BadgeIcon}>{iconJSX}</div>}
                <div className={styles.Title}>{eventInfo.event.title}</div>
            </div>
            {eventInfo.event.extendedProps.number === '' ? (
                (eventInfo.event.extendedProps.type === CalendarEventType.REGULAR_REQUEST ||
                    eventInfo.event.extendedProps.type === CalendarEventType.REQUEST) && (
                    <div className={styles.BadgeIcon}>
                        <HourglassBottom className={styles.Icon} />
                    </div>
                )
            ) : (
                <div className={styles.Badge}>{eventInfo.event.extendedProps.number}</div>
            )}
        </div>
    )
}

export function renderEventAgendaView(event: EventCalendar, selectEvent: (event: EventCalendar) => void): JSX.Element {
    if (event.type === CalendarEventType.RATE_SLOT) return renderEventGlobalRateAgenda(event, selectEvent)

    let iconJSX: JSX.Element | undefined
    let occasionalSlots: OccasionalSlot[] = []

    switch (event.type) {
        case CalendarEventType.EVENT:
            iconJSX = (event.slots as EventDay).booked ? <Check className={styles.Icon} /> : undefined
            break
        case CalendarEventType.TASK_NOTE:
            iconJSX = defineTaskNoteTypeIcon((event.slots as TaskNote).type)
            break
        case CalendarEventType.AVAILABLE:
        case CalendarEventType.VALIDATED:
        case CalendarEventType.REMAINDER_AVAILABLE:
        case CalendarEventType.EXPIRED:
        case CalendarEventType.SCHEDULED:
            occasionalSlots = event.slots as OccasionalSlot[]
            iconJSX =
                occasionalSlots.length > 0 && occasionalSlots[0].externalIcon
                    ? getOccasionalSlotsIconTextToJSX(occasionalSlots[0].externalIcon, styles.Icon)
                    : undefined
            break
        default:
            iconJSX = undefined
            break
    }

    return (
        <div
            className={`${styles.CurrentEventContent} ${getBackgroundColor(event.type)}`}
            onClick={() => {
                selectEvent(event)
            }}
        >
            <div className={styles.IconAndTitle}>
                {iconJSX !== undefined && <div className={styles.BadgeIcon}>{iconJSX}</div>}
                <div className={`${styles.Title} ${iconJSX ? '' : styles.TitleWithoutIcon}`}>{event.title}</div>
            </div>
            {event.number === '' ? (
                (event.type === CalendarEventType.REGULAR_REQUEST || event.type === CalendarEventType.REQUEST) && (
                    <div className={styles.BadgeIcon}>
                        <HourglassBottom className={styles.Icon} />
                    </div>
                )
            ) : (
                <div className={styles.Badge}>{event.number}</div>
            )}
        </div>
    )
}

function renderEventGlobalRateAgenda(event: EventCalendar, selectEvent: (event: EventCalendar) => void): JSX.Element {
    const totalMinutes: number = moment.duration(moment(event.end).diff(moment(event.start))).asMinutes()
    return (
        <div
            className={`${styles.EventGlobalRate} ${styles.EventGlobalRateAgenda}`}
            onClick={() => {
                selectEvent(event)
            }}
        >
            <p className={styles.Text}>{event.title}</p>
            {event.slots &&
                (event.slots as RateSlot[]).length > 0 &&
                (event.slots as RateSlot[]).map((rateSlot: RateSlot, index: number) => {
                    const startMinutes: number = rateSlot.start
                        .split(':')
                        .reduce((acc: number, time: string) => 60 * acc + Number.parseInt(time, 10), 0)
                    const endMinutes: number = rateSlot.end
                        .split(':')
                        .reduce((acc: number, time: string) => 60 * acc + Number.parseInt(time, 10), 0)
                    const width: number = ((endMinutes - startMinutes) / totalMinutes) * 120

                    return (
                        <div
                            key={`${index}_${rateSlot.start}_${rateSlot.end}`}
                            className={defineRateSlotDivStyle(rateSlot.type)}
                            style={{ width: `${width}%` }}
                        ></div>
                    )
                })}
        </div>
    )
}

export function renderMoreLinkContent(
    info: MoreLinkContentArg,
    isMobile: boolean,
    t: (key: string) => string
): JSX.Element {
    return <div>{isMobile ? `+ ${info.num}` : `${t('more')} ${info.num}`}</div>
}

function getEventTypePriority(type: CalendarEventType): number {
    switch (type) {
        case CalendarEventType.EVENT:
            return 0
        case CalendarEventType.SCHEDULED:
            return 1
        case CalendarEventType.REQUEST:
            return 2
        case CalendarEventType.VALIDATED:
            return 3
        case CalendarEventType.AVAILABLE:
            return 4
        case CalendarEventType.REMAINDER_AVAILABLE:
            return 5
        case CalendarEventType.EXPIRED:
            return 6
        case CalendarEventType.REGULAR_REQUEST:
            return 7
        case CalendarEventType.REGULAR:
            return 8
        case CalendarEventType.ABSENCE:
            return 9
        default:
            return Infinity
    }
}

export function compareEventsByTypeForAgendaViewDaycare(eventA: EventCalendar, eventB: EventCalendar): number {
    return compareEventCalendar(eventA, eventB, false)
}

export function compareEventsByTypeForAgendaViewParent(eventA: EventCalendar, eventB: EventCalendar): number {
    return compareEventCalendar(eventA, eventB, true)
}

function compareEventCalendar(eventA: EventCalendar, eventB: EventCalendar, isParentView: boolean): number {
    let priorityA: number
    let priorityB: number

    if (isParentView) {
        priorityA = eventA.type === CalendarEventType.EVENT ? 0 : Infinity
        priorityB = eventB.type === CalendarEventType.EVENT ? 0 : Infinity
    } else {
        priorityA = getEventTypePriority(eventA.type)
        priorityB = getEventTypePriority(eventB.type)
    }

    if (priorityA < priorityB) {
        return -1
    } else if (priorityA > priorityB) {
        return 1
    } else {
        if (isParentView) {
            const startA: number = new Date(eventA.start).getTime()
            const startB: number = new Date(eventB.start).getTime()

            if (startA < startB) {
                return -1
            } else if (startA > startB) {
                return 1
            } else {
                const endA: number = new Date(eventA.end).getTime()
                const endB: number = new Date(eventB.end).getTime()

                if (endA < endB) {
                    return -1
                } else if (endA > endB) {
                    return 1
                } else {
                    return 0
                }
            }
        } else {
            const titleA: string = eventA.title?.toLowerCase() || ''
            const titleB: string = eventB.title?.toLowerCase() || ''

            if (titleA < titleB) {
                return -1
            } else if (titleA > titleB) {
                return 1
            } else {
                return 0
            }
        }
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function compareEventsByType(eventA: any, eventB: any): number {
    const priorityA: number = eventA.extendedProps ? getEventTypePriority(eventA.extendedProps.type) : Infinity
    const priorityB: number = eventB.extendedProps ? getEventTypePriority(eventB.extendedProps.type) : Infinity

    if (priorityA < priorityB) {
        return -1
    } else if (priorityA > priorityB) {
        return 1
    } else {
        const titleA: string = eventA.title?.toLowerCase() || ''
        const titleB: string = eventB.title?.toLowerCase() || ''

        if (titleA < titleB) {
            return -1
        } else if (titleA > titleB) {
            return 1
        } else {
            return 0
        }
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function compareEventsForParents(eventA: any, eventB: any): number {
    const priorityA: number = eventA.extendedProps.type === CalendarEventType.EVENT ? 0 : Infinity
    const priorityB: number = eventB.extendedProps.type === CalendarEventType.EVENT ? 0 : Infinity

    if (priorityA < priorityB) {
        return -1
    } else if (priorityA > priorityB) {
        return 1
    } else {
        const startA: number = new Date(eventA.extendedProps.start).getTime()
        const startB: number = new Date(eventB.extendedProps.start).getTime()

        if (startA < startB) {
            return -1
        } else if (startA > startB) {
            return 1
        } else {
            const endA: number = new Date(eventA.extendedProps.end).getTime()
            const endB: number = new Date(eventB.extendedProps.end).getTime()

            if (endA < endB) {
                return -1
            } else if (endA > endB) {
                return 1
            } else {
                return 0
            }
        }
    }
}

export function renderEventGlobalRate(eventInfo: EventContentArg, currentView: CalendarView): JSX.Element {
    const totalMinutes: number = moment
        .duration(moment(eventInfo.event.end, 'HH:mm').diff(moment(eventInfo.event.start, 'HH:mm')))
        .asMinutes()
    return eventInfo.event.extendedProps.type === CalendarEventType.CLOSING_DAY ? (
        <div
            className={`${currentView === CalendarView.OneDay ? styles.DayEventContent : styles.CurrentEventContent} 
						 ${getBackgroundColor(CalendarEventType.CLOSING_DAY)}`}
        >
            <div className={styles.IconAndTitle}>
                <div className={styles.Title}>{eventInfo.event.title}</div>
            </div>
        </div>
    ) : (
        <div className={currentView === CalendarView.OneDay ? styles.EventGlobalRateOneDay : styles.EventGlobalRate}>
            <p className={styles.Text}>{eventInfo.event.title}</p>
            {eventInfo.event.extendedProps.slots &&
                eventInfo.event.extendedProps.slots.length > 0 &&
                eventInfo.event.extendedProps.slots.map((rateSlot: RateSlot, index: number) => {
                    const startMinutes: number = rateSlot.start
                        .split(':')
                        .reduce((acc: number, time: string) => 60 * acc + Number.parseInt(time, 10), 0)
                    const endMinutes: number = rateSlot.end
                        .split(':')
                        .reduce((acc: number, time: string) => 60 * acc + Number.parseInt(time, 10), 0)
                    const width: number = ((endMinutes - startMinutes) / totalMinutes) * 120

                    return (
                        <div
                            key={`${index}_${rateSlot.start}_${rateSlot.end}`}
                            className={defineRateSlotDivStyle(rateSlot.type)}
                            style={
                                currentView === CalendarView.OneDay ? { height: `${width}%` } : { width: `${width}%` }
                            }
                        ></div>
                    )
                })}
        </div>
    )
}

function defineRateSlotDivStyle(type: RateType): string {
    if (type === RateType.success) {
        return styles.Success
    } else if (type === RateType.warning) {
        return styles.Warning
    } else if (type === RateType.error) {
        return styles.Error
    } else {
        return ''
    }
}

export function renderEventForRegularConflict(event: EventRegularOverlap): JSX.Element {
    return (
        <div className={`${styles.CurrentEventContent} ${getBackgroundColor(event.type)} ${styles.ConflictEvent}`}>
            <div className={styles.IconAndTitle}>
                <div className={`${styles.Title} ${styles.TitleWithoutIcon}`}>{event.title}</div>
            </div>
            {(event.type === CalendarEventType.REGULAR_REQUEST || event.type === CalendarEventType.REQUEST) && (
                <div className={styles.BadgeIcon}>
                    <HourglassBottom className={styles.Icon} />
                </div>
            )}
        </div>
    )
}

export function renderEventForMealTable(type: CalendarEventType, title: string): JSX.Element {
    return (
        <div className={`${styles.CurrentEventContent} ${getBackgroundColor(type)} ${styles.MealEvent}`}>
            <div className={styles.TitleContainer}>
                <p className={styles.Title}>{title}</p>
            </div>
        </div>
    )
}
